import {Room, RoomStore} from "../../stores/RoomStore";
import EventEmitter from "../../EventEmitter";
import {UserStore} from "../../stores/UserStore";
import ShadowGateway from "./ShadowGateway";
import {action, makeObservable, observable} from "mobx";
import {MessageStore} from "../../stores/MessageStore";
import Logger from "../../Logger";
import RestClient, {RequestOptions} from "../../RestClient";
import shadowConfig from "../../../config";
import config from "../../../config";
import {GatewayEvent} from "./GatewayEvent";
import {
    APIRoom,
    APIUser,
    AppData,
    BasicRoomData,
    ClientEvent,
    FormattedPayload,
    JoinData,
    RestShadowMessage,
    RoomData,
    ShadowPayload,
    ShadowTheme,
    ThemeData,
    UserPatchPayload
} from "./interfaces";
import {ShadowMessage as Message} from "./Message";
import {sleep} from "../../utils";
import {AxiosProgressEvent} from "axios";
import {APIEmbed} from "discord-api-types/v10";
import queryString from "query-string";
import uiStore from "../../stores/uiStore";
import {notifier} from "../../Notifier";

export interface PayloadJson {
    embeds?: APIEmbed[],
    content?: string,
}

export interface FileObject {
    name: string,
    blob: Blob | File
}

export type SendFilesOptions = RequestOptions & { payloadJson?: PayloadJson };

export class ShadowClient extends EventEmitter {
    restClient: RestClient;
    // readonly users: Map<string, APIUser> = new Map<string, APIUser>();
    // rooms: Map<string, APIRoom> = new Map<string, APIRoom>();
    connected: boolean = false;
    userId?: string;
    appData?: AppData = undefined;
    errors: any[];
    userStore: UserStore;
    messageStore: MessageStore;
    roomStore: RoomStore;
    joinedRoomId: string = null;

    private readonly gateway: ShadowGateway;
    private logger = new Logger("ShadowClient");

    // private readonly messages: Map<string, Message> = new Map<string, Message>();

    constructor() {
        super();
        this.connect = this.connect.bind(this);
        this.onGatewayMessage = this.onGatewayMessage.bind(this);
        this.onUserJoin = this.onUserJoin.bind(this);

        this.restClient = new RestClient({client: this, apiUrl: shadowConfig.apiUrl, name: "shadow"});

        this.gateway = new ShadowGateway(shadowConfig.wsUrl);

        this.connected = false;
        this.userId = null;
        this.errors = [];

        makeObservable(this, {
            joinedRoomId: observable,
            errors: observable,
            userId: observable,
            appData: observable,
            connected: observable,
            onGatewayMessage: action,
            connect: action,
        })

        this.gateway.on('disconnected', (message: any) => this.emit("disconnected", message));

        this.on('disconnected', () => {
            this.connected = false;
        })

        this.userStore = new UserStore(this);
        this.roomStore = new RoomStore(this, this.userStore);
        this.messageStore = new MessageStore(this, this.userStore);

        this.gateway.on('message', this.onGatewayMessage);

        document.addEventListener("visibilitychange", async () => {
            if (!document.hidden && this.joinedRoomId && this.connected) {
                await this.syncRoomData(this.joinedRoomId);

                const clientUser = this.getClientUser();

                //@todo first fetch user to check current room id?
                if (clientUser.room_id !== this.joinedRoomId) {
                    await this.joinRoomWithId(clientUser.room_id);
                }

                if (uiStore.debugModeEnabled) {
                    notifier.info("Synced room data")
                }
            }
        });

        window.sc = this;
    }

    getJoinedRoomId() {
        return this.joinedRoomId;
    }

    async onUserJoin(message: ShadowPayload) {
        const clientUser = this.getClientUser();
        const roomId = clientUser?.room_id;
        const userId = clientUser?.id
        const user = message['data']['user'];
        this.logger.trace("user %s joined room %s", `${user.username}[${user.id}]`, user['room_id']);

        if (user['id'] === userId && roomId !== user['room_id']) {
            this.logger.trace("joined user is client user. this client has not joined the room yet. auto joining room");
            await this.joinRoomWithId(user['room_id']);

            // history.push('/rooms/' + user['room_id']);
        }
    }

    async onGatewayMessage(payload: ShadowPayload) {
        try {
            switch (payload.type) {

                case GatewayEvent.MESSAGE_CREATE: {
                    const restMessage: RestShadowMessage = payload.data;
                    // const message = this.upsertMessage(restMessage);
                    // const author = message.author;
                    // const user = this.userStore.get(author.id);

                    const user = this.userStore.get(restMessage.author_id);
                    if (user?.typing) {
                        if (user.typingTimeout) {
                            window.clearTimeout(user.typingTimeout);
                            user.typingTimeout = null;
                        }
                        user.typing = false;
                    }

                    // this.emit(GatewayEvent.MESSAGE_CREATE, message);
                    break;
                }

                case GatewayEvent.ROOM_CREATE: {
                    // this.upsertRoom(payload.data.room)
                    break;
                }
                case GatewayEvent.ROOM_DELETE: {
                    // this.rooms.delete(payload.data.id);
                    break;
                }
                case GatewayEvent.IDENTIFY:
                    if (payload.error) {
                        this.logger.error("Error while trying to Identify", payload.error);
                        break;
                    }
                    this.connected = true;
                    this.emit("connected");
                    break;
                case "USER_KICKED":
                    const {
                        roomId: roomIdKicked,
                        kickedUserId
                    } = payload.data;
                    this.logger.trace("User kicked %s from room %s", kickedUserId, roomIdKicked);
                    this.joinedRoomId = null;
                    break;
                case GatewayEvent.USER_CREATE: {
                    // this.upsertUser(payload.data.user);
                    break;
                }
                case GatewayEvent.USER_UPDATE: {
                    // this.upsertUser(payload.data.user)
                    break;
                }
                case GatewayEvent.USER_JOIN: {
                    await this.onUserJoin(payload);
                    // this.upsertUser(payload.data.user);
                    break;
                }
                case GatewayEvent.USER_LEAVE: {
                    const user = this.userStore.get(payload.data.user_id);
                    if (user && user.room_id === payload.data.room_id) {
                        user.room_id = null;
                    }
                    break;
                }
                case GatewayEvent.TYPING:
                    const user = this.userStore.get(payload.data.userId);
                    user.typing = true;

                    if (user.typingTimeout) {
                        clearTimeout(user.typingTimeout);
                    }

                    user.typingTimeout = window.setTimeout(() => {
                        user.typing = false;
                        user.typingTimeout = null;
                    }, 10000);

                    break;
            }
            this.emit("gateway-event", payload);
        } catch (e: any) {
            this.logger.log("Error handling shadow payload with type %s and data %s", payload.type, JSON.stringify(payload.data));
        }
    }


    async fetchClientData() {
        this.appData = await this.getAppData();
        // const user = this.appData.user
        // this.upsertUser(user);
        this.userStore.upsertUser(this.appData.user);
    }

    async joinRoomWithId(roomId: string, inviteToken?: string): Promise<RoomData> {
        if (this.joinedRoomId === roomId) {
            throw new Error("Already joined room " + roomId);
        }

        this.logger.trace("joining room %s", roomId);

        const clientId = this.gateway.id;

        try {
            const joinData: JoinData = {clientId}

            if (inviteToken) {
                joinData.inviteToken = inviteToken;
            }

            await this.post('/rooms/' + roomId + '/join', joinData);
            const clientUser = this.getClientUser();
            clientUser["room_id"] = roomId;
        } catch (e: any) {
            this.logger.error(e.message);
            throw e;
        }
        this.joinedRoomId = roomId;

        const data = await this.syncRoomData(roomId);
        const token = this.getQueryToken();
        const url = "/rooms/" + data.room.id + (token ? "?token=" + token : "");
        window.history.replaceState({}, data.room.name, url);

        return data
    }

    async connect(loginToken: string) {
        if (this.connected) {
            throw Error("already connected")
        }

        const result = await this.gateway.connect(loginToken);
        await this.fetchClientData();
        this.roomStore.loadRooms();

        // const rooms = await this.getRooms();
        // this.rooms = createMap(rooms);

        if (result && result.session) {
            this.userId = result.session.userId;
        } else {
            throw new Error("Did not receive session data upon connecting to ws server.")
        }

        const clientUser = this.getClientUser();
        clientUser.login_token = loginToken;

        this.connected = true;
        return result;
    }

    async logout() {
        config.apiToken = undefined;

        const debug = localStorage.getItem("debug");
        localStorage.clear();

        //used by the debug logging library
        if (debug) {
            localStorage.setItem("debug", debug);
        }

        await this.disconnect();
    }

    getClientUser() {
        return this.userId && this.userStore.get(this.userId);
    }

    disconnect() {
        this.joinedRoomId = null;
        return this.gateway.disconnect();
    }

    // upsertRoom(room: APIRoom) {
    //     this.upsertItem(this.rooms, room);
    // }
    //
    // upsertUser(user: APIUser) {
    //     this.upsertItem(this.users, user);
    // }
    //
    // upsertMessage(restMessage: RestShadowMessage) {
    //     const author = this.users.get(restMessage.author_id);
    //     const room = this.rooms.get(restMessage.room_id);
    //
    //     if (!author) {
    //         throw new Error(`Missing author with id ${restMessage.author_id} `)
    //     }
    //
    //     if (!room) {
    //         throw new Error(`Missing room with id ${restMessage.room_id} `)
    //     }
    //
    //     const message = new Message({
    //         ...restMessage,
    //         client: this,
    //         room,
    //         author,
    //     });
    //
    //     this.upsertItem(this.messages, message);
    //     return message;
    // }
    //
    // upsertItem(items: Map<string, { id: string }>, item: { id: string }) {
    //     let storedItem = items.get(item.id)
    //     if (!storedItem) {
    //         items.set(item.id, item);
    //     } else {
    //         Object.assign(storedItem, item);
    //     }
    // }

    post(route: string, data?: any, progressCallback?: (data: AxiosProgressEvent) => any) {
        return this.restClient.post(route, data, {progressCallback});
    }

    get(route: string, data?: any) {
        return this.restClient.get(route, data);
    }

    put(route: string, data?: any, progressCallback?: (data: AxiosProgressEvent) => any) {
        return this.restClient.put(route, data, {progressCallback});
    }

    patch(route: string, data?: any, progressCallback?: (data: AxiosProgressEvent) => any) {
        return this.restClient.patch(route, data, {progressCallback});
    }

    delete(route: string, data?: any) {
        return this.restClient.delete(route, data);
    }

    postTheme(theme: ThemeData): Promise<ShadowTheme> {
        return this.post("/themes", theme);
    }

    getThemes(): Promise<ShadowTheme[]> {
        return this.get("/themes");
    }

    getAppData(): Promise<AppData> {
        return this.get('/app/data');
    }

    getRoomData(roomId: string): Promise<RoomData> {
        return this.get('/rooms/' + roomId + '/data');
    }

    async registerAnonymously(username: string): Promise<APIUser> {
        const {user} = await this.post('/register', {username, type: "anonymous"});
        return user;
    }

    async postMessage(content: string) {
        const roomId = this.joinedRoomId;

        if (!roomId) {
            throw new Error("Client hasn't joined a room yet")
        }

        return this.post('/rooms/' + roomId + '/messages', {
            content,
        });
    }

    getMessagesInRoom(roomId: string) {
        return this.get(`/rooms/${roomId}/messages`);
    }

    patchRoom(roomId: string, data: Partial<APIRoom>) {
        return this.patch(
                `/rooms/${roomId}`,
                data
        );
    }

    deleteRoom(roomId: string) {
        return this.delete("/rooms/" + roomId);
    }

    patchUser(data: Partial<APIUser> & { base64avatar: string }) {
        return this.patch(`/users/@me`, data);
    }

    async getRooms(): Promise<APIRoom[]> {
        const {rooms} = await this.get('/rooms');
        return rooms;
    }

    sendFormData(
            channelId: string,
            files: { name: string, blob: Blob | File }[],
            payloadJson: PayloadJson | null,
            options: RequestOptions = {}
    ): Promise<Message> {

        let form = new FormData();

        for (const file of files) {
            form.append('file', file.blob, file.name);
        }

        if (payloadJson) {
            form.append('payload_json', JSON.stringify(payloadJson));
        }

        return this.restClient.post('/rooms/' + channelId + '/messages', form, options)
    }

    async changeProfile(data: UserPatchPayload) {
        return this.restClient.patch('/users/@me', data);
    }

    createAvatarUrl(userId: string, avatarId: string) {
        return 'https://cdn.discordapp.com/avatars/' + userId + '/' + avatarId + '.png';
    }

    async patchCurrentMemberNick(guildId: string, data: any) {
        return this.restClient.patch(`/guilds/${guildId}/members/@me/nick`, data,);
    }

    triggerTyping(channelId: string) {
        return this.restClient.post('/rooms/' + channelId + '/typing', null);
    }

    async simulateUploads(files: FileObject[], options: SendFilesOptions) {
        const totalSize = files.map(file => file.blob.size)
                .reduce((acc, size) => acc + size, 0);
        const maxSpeedMbs = 1;
        const maxSleepMs = 500;
        let uploaded = 0;

        while (uploaded < totalSize) {
            const sleepMs = Math.random() * maxSleepMs;
            const speedMbs = Math.random() * maxSpeedMbs;
            await sleep(sleepMs);
            // const mbLeft = totalSize - uploaded;
            const deltaB = speedMbs * 1000 * sleepMs;
            uploaded = Math.min(totalSize, uploaded + deltaB);

            options.progressCallback({
                loaded: uploaded,
                total: totalSize,
                bytes:
                deltaB,
                lengthComputable: true
            })
        }
    }

    async sendFiles(channelId: string, files: FileObject[], options: SendFilesOptions = {}): Promise<Message> {
        const {
            payloadJson = null,
        } = options;

        if (config.simulateUploads && options.progressCallback) {
            try {
                await this.simulateUploads(files, options)

            } catch (e: any) {
                this.logger.error("Error while simulating file uploads. Error: %s", e);
                throw e;
            }

            const oldCallback = options.progressCallback;

            options.progressCallback = (event) => {
                event.loaded = event.total;
                oldCallback(event);
            }
        }

        return this.sendFormData(channelId, files, payloadJson, options)
    }

    async tokenLogin(loginToken: string): Promise<string> {
        const response = await this.post("/token_login", {loginToken});
        return response.apiKey;
    }

    async sendDM(userId: string, body: FormattedPayload): Promise<Message> {
        throw new Error("Not implemented");
        // const channel = await this.getDmChannel(userId);
        // return this.sendMessage(body, channel.id);
    }

    editMessage(payload: FormattedPayload, roomId: string, messageId: string) {
        return this.patch('/rooms/' + roomId + '/messages/' + messageId, payload);
    }

    deleteMessage(roomtId: string, messageId: string) {
        return this.delete('/rooms/' + roomtId + '/messages/' + messageId)
    }

    async addReaction(roomId: string, messageId: string, emojiId: string) {
        return this.put('/rooms/' + roomId + '/messages/' + messageId + '/reactions/' + emojiId);
    }

    async deleteReaction(roomId: string, messageId: string, emojiId: string) {
        return this.delete('/rooms/' + roomId + '/messages/' + messageId + '/reactions/' + emojiId);
    }

    async createRoom() {
        const room: APIRoom = await this.post("/rooms");
        return this.roomStore.upsertRoom(room);
    }

    kickUser(roomId: string, userId: string) {
        return this.post(`/rooms/${roomId}/kick`, {userId});
    }

    async leaveRoom(roomId: string) {
        return this.post(`/rooms/${roomId}/leave`);
    }

    getJoinedRoom(): Room | undefined {
        return this.roomStore.get(this.getJoinedRoomId());
    }

    async resetInviteToken(roomId: string): Promise<string> {
        const result = await this.post('/rooms/' + roomId + '/invites?action=resetInviteToken');
        return result.token;
    }

    async joinRoomWithInvite(invite: string) {
        const roomData = await shadowClient.getRoomWithInvite(invite);
        return this.joinRoomWithId(roomData.room_id, invite);
    }

    getRoomWithInvite(inviteId: string): Promise<BasicRoomData> {
        return this.get('/invites/' + inviteId);
    }

    getQueryToken() {
        let {token} = queryString.parse(window.location.search) as { token: string };

        if (Array.isArray(token)) {
            token = token[0];
        }
        return token;
    }

    private async syncRoomData(roomId: string): Promise<RoomData> {
        const data = await this.getRoomData(roomId)
        const {room, users, messages} = data;

        // this.upsertRoom(room);
        // users.forEach(user => this.upsertUser(user));
        // messages.forEach(restMessage => this.upsertMessage(restMessage));
        this.roomStore.upsertRoom(room);
        this.userStore.setUsers(users);
        this.messageStore.setMessages(messages);
        this.logger.trace("synced room data for room %s", roomId);
        this.emit(ClientEvent.ROOM_SYNCED, {messages: this.messageStore.messages});
        return data;
    }

}

const shadowClient = new ShadowClient();
export default shadowClient;
